import { multipleSpacesRegEx } from "@@/utilities/regexes";
import type { Except } from "type-fest";
import { z } from "zod";
import type { GeoLocation} from "./geo-location";
import { geoLocationZodSchema } from "./geo-location";
import type { MapUrl} from "./map-url";
import { mapUrlZodSchema } from "./map-url";

const addressZodSchema: z.ZodSchema<Address> = z.object({
    _type: z.literal("ADDRESS"),
    addressRows: z.array(z.string().min(1)).nonempty("gatuadress saknas"),
    zipCode: z
        .string()
        .min(5, "postnummer saknas")
        .transform(zipCode =>
            zipCode.replace(/^\s*(\d*)\s+(\d*)/, "$1$2").trim(),
        ), // transform zipcode to XXXXX format,
    city: z
        .string()
        .min(1, "postort saknas")
        .transform(city => city.replace(multipleSpacesRegEx, " ").trim()), // remove double spaces
    country: z.enum(["SE"]),
    mapUrls: mapUrlZodSchema.array(),
    geoLocation: geoLocationZodSchema.optional(),
});

type Address = {
    readonly _type: "ADDRESS";
    readonly addressRows: string[];
    readonly zipCode: string;
    readonly city: string;
    readonly country: string;
    readonly geoLocation?: GeoLocation;
    readonly mapUrls: MapUrl[];
};

const addressFactory = (params: Except<Address, "_type">): Address => {
    return {
        ...params,
        _type: "ADDRESS",
    };
};

const emptyAddress: Address = addressFactory({
    addressRows: [],
    city: "",
    country: "",
    mapUrls: [],
    zipCode: "",
});

const parseAddress = (address: string | undefined): Address => {
    if (!address) {
        const message =
            "Address must at least contain one address row and one row with zip and city; " +
            address;
        // eslint-disable-next-line no-console
        console.error(message);
        throw new Error(message);
    }

    const step1 = address.split(",").map(item => item.trim());
    if (step1.length < 2) {
        const message =
            "Address must at least contain one address row and one row with zip and city; " +
            address;
        // eslint-disable-next-line no-console
        console.error(message);
        throw new Error(message);
    }
    const zipAndCity = step1.pop() as string;
    const addressRows = step1;

    const [zipCode, city] = zipAndCity
        .replace(/^\s*(\d*)\s+(\d*)/, "$1$2 ") // set zipcode to XXXXX format
        .replace(/\s+/, " ") // make sure there are no double spaces
        .split(" ")
        .map(item => item.trim());

    if (!zipCode || !city) {
        const message =
            "Address contain one row (at the end) with both zip and city; " +
            zipAndCity;
        // eslint-disable-next-line no-console
        console.error(message);
        throw new Error(message);
    }

    return addressFactory({
        addressRows,
        zipCode,
        city,
        country: "SE",
        mapUrls: [],
    });
};

const formatOneLineAddress = (address?: Address): string => {
    if (!address || !address.addressRows[0]) return "";
    return `${address.addressRows[0]}, ${address.zipCode} ${address.city}, ${address.country}`;
};

export {
    addressFactory,
    addressZodSchema,
    emptyAddress,
    formatOneLineAddress,
    parseAddress,
};
export type { Address };
